/**
 * Model catalogue + pricing for the live token & monthly spend estimator.
 *
 * Prices are USD per 1M tokens (list prices) for flagship and mini/flash tier
 * models across OpenAI, Anthropic and Google.
 */

import type { ApiProviderId } from './types';

export type ModelTier = 'flagship' | 'mini';

export interface ModelSpec {
  id: string;
  label: string;
  provider: ApiProviderId;
  providerLabel: string;
  tier: ModelTier;
  /** USD per 1M input tokens. */
  input: number;
  /** USD per 1M output tokens. */
  output: number;
  /** Default model id used when calling this vendor's API. */
  chatModel: string;
}

export const MODELS: ModelSpec[] = [
  // — OpenAI —
  {
    id: 'openai-gpt-5',
    label: 'GPT-5',
    provider: 'openai',
    providerLabel: 'OpenAI',
    tier: 'flagship',
    input: 1.25,
    output: 10,
    chatModel: 'gpt-5',
  },
  {
    id: 'openai-gpt-5-mini',
    label: 'GPT-5 mini',
    provider: 'openai',
    providerLabel: 'OpenAI',
    tier: 'mini',
    input: 0.25,
    output: 2,
    chatModel: 'gpt-5-mini',
  },
  {
    id: 'openai-gpt-4.1',
    label: 'GPT-4.1',
    provider: 'openai',
    providerLabel: 'OpenAI',
    tier: 'flagship',
    input: 2,
    output: 8,
    chatModel: 'gpt-4.1',
  },
  {
    id: 'openai-gpt-4.1-mini',
    label: 'GPT-4.1 mini',
    provider: 'openai',
    providerLabel: 'OpenAI',
    tier: 'mini',
    input: 0.4,
    output: 1.6,
    chatModel: 'gpt-4.1-mini',
  },
  {
    id: 'openai-gpt-4o',
    label: 'GPT-4o',
    provider: 'openai',
    providerLabel: 'OpenAI',
    tier: 'flagship',
    input: 2.5,
    output: 10,
    chatModel: 'gpt-4o',
  },
  {
    id: 'openai-gpt-4o-mini',
    label: 'GPT-4o mini',
    provider: 'openai',
    providerLabel: 'OpenAI',
    tier: 'mini',
    input: 0.15,
    output: 0.6,
    chatModel: 'gpt-4o-mini',
  },

  // — Anthropic —
  {
    id: 'anthropic-opus-4',
    label: 'Claude Opus 4',
    provider: 'anthropic',
    providerLabel: 'Anthropic',
    tier: 'flagship',
    input: 15,
    output: 75,
    chatModel: 'claude-opus-4-20250514',
  },
  {
    id: 'anthropic-sonnet-4',
    label: 'Claude Sonnet 4',
    provider: 'anthropic',
    providerLabel: 'Anthropic',
    tier: 'flagship',
    input: 3,
    output: 15,
    chatModel: 'claude-sonnet-4-20250514',
  },
  {
    id: 'anthropic-haiku-3.5',
    label: 'Claude Haiku 3.5',
    provider: 'anthropic',
    providerLabel: 'Anthropic',
    tier: 'mini',
    input: 0.8,
    output: 4,
    chatModel: 'claude-3-5-haiku-latest',
  },

  // — Google —
  {
    id: 'google-gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    provider: 'gemini',
    providerLabel: 'Google',
    tier: 'flagship',
    input: 1.25,
    output: 10,
    chatModel: 'gemini-2.5-pro',
  },
  {
    id: 'google-gemini-2.5-flash',
    label: 'Gemini 2.5 Flash',
    provider: 'gemini',
    providerLabel: 'Google',
    tier: 'mini',
    input: 0.3,
    output: 2.5,
    chatModel: 'gemini-2.5-flash',
  },
  {
    id: 'google-gemini-2.5-flash-lite',
    label: 'Gemini 2.5 Flash-Lite',
    provider: 'gemini',
    providerLabel: 'Google',
    tier: 'mini',
    input: 0.1,
    output: 0.4,
    chatModel: 'gemini-2.5-flash-lite',
  },
];

export const CALL_TIERS = [1_000, 100_000] as const;

/** Cost of a single call in USD. */
export function costPerCall(model: ModelSpec, inputTokens: number, outputTokens: number): number {
  const inputCost = (Math.max(0, inputTokens) / 1_000_000) * model.input;
  const outputCost = (Math.max(0, outputTokens) / 1_000_000) * model.output;
  return inputCost + outputCost;
}

/** Cost of `calls` identical calls in USD. */
export function costFor(model: ModelSpec, inputTokens: number, outputTokens: number, calls: number): number {
  return costPerCall(model, inputTokens, outputTokens) * Math.max(0, calls);
}

export interface CostRow {
  model: ModelSpec;
  perCall: number;
  per1k: number;
  per100k: number;
}

export function buildCostTable(inputTokens: number, outputTokens: number): CostRow[] {
  return MODELS.map((model) => ({
    model,
    perCall: costPerCall(model, inputTokens, outputTokens),
    per1k: costFor(model, inputTokens, outputTokens, 1_000),
    per100k: costFor(model, inputTokens, outputTokens, 100_000),
  }));
}

/** `$0.0042` — always 4 decimals so sub-cent figures stay visible. */
export function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value !== 0 && value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

/** Big monthly numbers: `$1,234.50`. */
export function formatUsdFull(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const decimals = value >= 100 ? 2 : value >= 1 ? 2 : 4;
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

export const PROVIDER_LABELS: Record<ApiProviderId, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Google Gemini',
};

/** Per-vendor completion model choices surfaced in the provider settings modal. */
export const CHAT_MODELS: Record<ApiProviderId, string[]> = {
  openai: ['gpt-5', 'gpt-5-mini', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini'],
  anthropic: ['claude-opus-4-20250514', 'claude-sonnet-4-20250514', 'claude-3-7-sonnet-latest', 'claude-3-5-haiku-latest'],
  gemini: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite'],
};
